import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import {
    REGELWERK_EREIGNIS,
    fasseStellenZusammen,
    herkunftFuer,
    meldeRegelwerkDialog,
    regelwerkZiele,
    uebernimmBestaetigte,
    zielBeschriftungen,
    zielFuerStelle,
    type PfadSchritt,
    type RegelwerkAnfrage,
} from '../services/regelwerkHinweis';

/** Überschrift je KI-Aktion; sonst „KI-Vorschlag". */
const AKTION_TITEL: Record<string, string> = {
    generateSafetyText: 'Mängeltext',
    analyzeInspectionPhoto: 'Mängelfoto',
    generateWorkSteps: 'SiGe-Plan: Arbeitsschritte',
    generateSigePlanHazards: 'SiGe-Plan: Gefährdungen',
    assistSigePlan: 'SiGe-Plan-Assistent',
    assistKoordinierung: 'Koordinierung',
    assistHandbuch: 'SiGe-Handbuch',
    generateUfsaEntry: 'Unterlage RAB 32: Bauteil',
    generateRabSuggestion: 'Unterlage RAB 32: Bauteil',
    generateUfsaCatalog: 'Unterlage RAB 32: Katalog',
    generateCatalogFromDescription: 'Unterlage RAB 32: Katalog',
    analyzeHazardImage: 'Unterlage RAB 32: Foto',
    analyzeHazardSpot: 'Unterlage RAB 32: Bildausschnitt',
    predictMutualHazards: 'Gefährdungsmatrix',
    chatSigePlan: 'SiGe-Plan-Chat',
    gbuVorschlag: 'Gefährdungsbeurteilung: Vorschlag',
    gbuPruefen: 'Gefährdungsbeurteilung: Prüfung',
};

const zielSchluessel = (pfad: PfadSchritt[] | null) => (pfad ? JSON.stringify(pfad) : '');

interface Zeile {
    angabe: string;
    herkunft: string;
    /** Ziel als JSON-Pfad; "" = nicht übernehmen möglich. */
    ziel: string;
    uebernehmen: boolean;
    zuordenbar: boolean;
}

/**
 * Dialog für Vorschriften, die der gemini-proxy aus einem KI-Vorschlag
 * entfernt hat, weil sie sich an keiner amtlichen Quelle bestätigen ließen
 * (services/regelwerkHinweis.ts).
 *
 * Er erscheint, BEVOR der Vorschlag beim Aufrufer ankommt: je Vorschrift der
 * Eintrag, aus dem sie stammt („3. Gerüst aufbauen"), eine Auswahl „zuweisen
 * zu" und ein Häkchen „selbst geprüft, übernehmen". Nur Angehaktes kommt
 * zurück in den Vorschlag, und zwar in das gewählte Feld. Die Bestätigung gilt
 * nur für diesen Vorschlag.
 *
 * Kommen mehrere Vorschläge kurz hintereinander (Sammel-Vorschlag im
 * SiGe-Plan), stehen sie in einer Warteschlange und erscheinen nacheinander.
 */
export const KiRegelwerkHinweis: React.FC = () => {
    // Jede Anfrage mit fester Nummer: der offene Dialog behält seine Häkchen,
    // auch wenn hinten weitere Anfragen dazukommen.
    const [schlange, setSchlange] = useState<{ nr: number; anfrage: RegelwerkAnfrage }[]>([]);
    // Offene Anfragen SOFORT im Ereignis registriert, nicht erst nach dem
    // Rendern: sonst kennte das Abmelden eine gerade eingetroffene Anfrage
    // nicht, und ihr Aufrufer wartete ewig (Codex-Review 13.10.).
    const offen = useRef<RegelwerkAnfrage[]>([]);
    const naechsteNr = useRef(0);

    useEffect(() => {
        const aufnehmen = (e: Event) => {
            const anfrage = (e as CustomEvent<RegelwerkAnfrage>).detail;
            if (!anfrage?.stellen?.length || typeof anfrage.fertig !== 'function') return;
            const nr = naechsteNr.current++;
            offen.current.push(anfrage);
            setSchlange(alt => [...alt, { nr, anfrage }]);
        };
        window.addEventListener(REGELWERK_EREIGNIS, aufnehmen);
        meldeRegelwerkDialog(true);
        return () => {
            meldeRegelwerkDialog(false);
            window.removeEventListener(REGELWERK_EREIGNIS, aufnehmen);
            // Niemand darf auf eine Antwort warten, die nie kommt.
            offen.current.forEach(anfrage => anfrage.fertig(anfrage.daten));
            offen.current = [];
        };
    }, []);

    const aktuell = schlange[0];
    if (!aktuell) return null;
    return (
        <RegelwerkDialog
            key={aktuell.nr}
            anfrage={aktuell.anfrage}
            weitere={schlange.length - 1}
            fertig={(daten) => {
                aktuell.anfrage.fertig(daten);
                offen.current = offen.current.filter(a => a !== aktuell.anfrage);
                setSchlange(alt => alt.filter(e => e.nr !== aktuell.nr));
            }}
        />
    );
};

const RegelwerkDialog: React.FC<{
    anfrage: RegelwerkAnfrage;
    weitere: number;
    fertig: (daten: unknown) => void;
}> = ({ anfrage, weitere, fertig }) => {
    const ziele = useMemo(() => regelwerkZiele(anfrage.daten), [anfrage.daten]);
    const beschriftungen = useMemo(() => zielBeschriftungen(ziele), [ziele]);
    const [zeilen, setZeilen] = useState<Zeile[]>(() => fasseStellenZusammen(anfrage.stellen).map(stelle => {
        const ziel = zielFuerStelle(anfrage.daten, stelle);
        return {
            angabe: stelle.angabe,
            herkunft: herkunftFuer(anfrage.daten, stelle),
            ziel: zielSchluessel(ziel),
            uebernehmen: false,
            zuordenbar: ziele.length > 0,
        };
    }));
    const ersterKnopf = useRef<HTMLButtonElement>(null);
    useEffect(() => { ersterKnopf.current?.focus(); }, []);

    const ohne = () => fertig(anfrage.daten);
    const gewaehlt = zeilen.filter(z => z.uebernehmen && z.ziel);
    const uebernehmen = () => fertig(uebernimmBestaetigte(
        anfrage.daten,
        gewaehlt.map(z => ({ angabe: z.angabe, ziel: JSON.parse(z.ziel) as PfadSchritt[] })),
    ));
    const aendere = (i: number, teil: Partial<Zeile>) =>
        setZeilen(alt => alt.map((z, j) => (j === i ? { ...z, ...teil } : z)));

    // Nach Herkunft gruppiert, in der Reihenfolge des ersten Auftretens.
    const gruppen = new Map<string, number[]>();
    zeilen.forEach((z, i) => gruppen.set(z.herkunft, [...(gruppen.get(z.herkunft) ?? []), i]));

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 p-4 print:hidden"
            onKeyDown={(e) => { if (e.key === 'Escape') ohne(); }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="ki-regelwerk-titel"
                className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
            >
                <div className="flex items-start gap-3 border-b border-slate-200 p-4">
                    <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
                    <div className="min-w-0 text-sm text-slate-700">
                        <h2 id="ki-regelwerk-titel" className="text-base font-bold text-slate-900">
                            {AKTION_TITEL[anfrage.aktion] ?? 'KI-Vorschlag'}: Vorschriften nicht bestätigt
                        </h2>
                        <p className="mt-1">
                            Diese Vorschriften ließen sich an keiner amtlichen Quelle bestätigen und wurden aus dem
                            Vorschlag entfernt. Übernimm eine nur, wenn du sie selbst geprüft hast. Das gilt nur für
                            diesen Vorschlag.
                        </p>
                    </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto p-4">
                    <div className="grid gap-4">
                        {[...gruppen.entries()].map(([herkunft, indizes]) => (
                            <section key={herkunft} className="grid gap-2">
                                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">aus: {herkunft}</h3>
                                {indizes.map(i => {
                                    const z = zeilen[i];
                                    const id = `ki-regelwerk-${i}`;
                                    return (
                                        <div key={id} className="grid gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-[1fr_auto] sm:items-center">
                                            <label htmlFor={`${id}-ok`} className="flex min-w-0 items-start gap-2 text-sm">
                                                <input
                                                    id={`${id}-ok`}
                                                    type="checkbox"
                                                    className="mt-0.5 h-4 w-4 shrink-0"
                                                    checked={z.uebernehmen}
                                                    disabled={!z.zuordenbar || !z.ziel}
                                                    onChange={(e) => aendere(i, { uebernehmen: e.target.checked })}
                                                />
                                                <span className="min-w-0 break-words">
                                                    <span className="font-semibold text-slate-900">{z.angabe}</span>
                                                    <span className="block text-xs text-slate-500">selbst geprüft, übernehmen</span>
                                                </span>
                                            </label>
                                            {z.zuordenbar ? (
                                                <label htmlFor={`${id}-ziel`} className="grid gap-1 text-xs text-slate-500">
                                                    zuweisen zu
                                                    <select
                                                        id={`${id}-ziel`}
                                                        className="max-w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 sm:max-w-[16rem]"
                                                        value={z.ziel}
                                                        onChange={(e) => aendere(i, { ziel: e.target.value, uebernehmen: e.target.value ? z.uebernehmen : false })}
                                                    >
                                                        <option value="">— nicht übernehmen —</option>
                                                        {ziele.map((ziel, k) => (
                                                            <option key={zielSchluessel(ziel.pfad)} value={zielSchluessel(ziel.pfad)}>
                                                                {beschriftungen[k]}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                            ) : (
                                                <span className="text-xs text-slate-500">Kein Feld für Vorschriften — bei Bedarf selbst eintragen.</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </section>
                        ))}
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 p-4">
                    {weitere > 0 && (
                        <span className="mr-auto text-xs text-slate-500">{weitere === 1 ? 'danach 1 weiterer Vorschlag' : `danach ${weitere} weitere Vorschläge`}</span>
                    )}
                    <button
                        ref={ersterKnopf}
                        type="button"
                        onClick={ohne}
                        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                        Ohne diese weiter
                    </button>
                    <button
                        type="button"
                        onClick={uebernehmen}
                        disabled={gewaehlt.length === 0}
                        className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {gewaehlt.length > 0 ? `${gewaehlt.length} geprüfte übernehmen` : 'Geprüfte übernehmen'}
                    </button>
                </div>
            </div>
        </div>
    );
};
